import React, { useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ApiOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import RestoreOptions, { DEFAULT_RESTORE_OPTIONS, buildIdsPayload } from "./RestoreOptions";
import RestoreResult from "./RestoreResult";
import { BACKUPS_ENDPOINT, DB_ENGINE, ENGINE_OPTIONS, RESTORE_MODE } from "../constants";

const { Text, Paragraph } = Typography;

/**
 * Pulls rows out of a database on another server. The operator supplies credentials, picks
 * a catalog, sees exactly how its tables line up against the live schema, and only then
 * chooses what to import.
 *
 * Credentials are posted per request and never stored — every step re-sends them.
 */
const ExternalRestorePanel = ({ onImported }) => {
  const { mutate } = useMutations();
  const { modal } = AntdApp.useApp();
  const [form] = Form.useForm();

  const [busy, setBusy] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [tableInfo, setTableInfo] = useState(null);
  const [options, setOptions] = useState(DEFAULT_RESTORE_OPTIONS);
  const [result, setResult] = useState(null);

  const engine = Form.useWatch("engine", form) ?? DB_ENGINE.SQL_SERVER;
  const database = Form.useWatch("database", form);
  const [archiveSource, setArchiveSource] = useState(true);

  /** Everything the API needs to reach the source, straight off the form. */
  const connection = () => {
    const values = form.getFieldsValue();
    return {
      engine: values.engine ?? DB_ENGINE.SQL_SERVER,
      host: (values.host || "").trim(),
      port: values.port ?? null,
      database: values.database || null,
      username: (values.username || "").trim(),
      password: values.password || "",
      encrypt: values.encrypt ?? true,
      trustServerCertificate: values.trustServerCertificate ?? true,
    };
  };

  const requireConnection = async () => {
    await form.validateFields(["host", "username", "password"]);
    return connection();
  };

  const testConnection = async () => {
    const payload = await requireConnection();
    setBusy("test");
    setTestResult(null);

    await mutate("testExternal", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/external/test`,
      payload,
      onSuccess: setTestResult,
    });

    setBusy(null);
  };

  const loadDatabases = async () => {
    const payload = await requireConnection();
    setBusy("databases");

    await mutate("externalDatabases", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/external/databases`,
      payload,
      onSuccess: (data) => setDatabases(data?.databases || []),
    });

    setBusy(null);
  };

  const loadTables = async () => {
    const payload = await requireConnection();
    if (!payload.database) {
      modal.warning({ title: "Pick a database", content: "Choose the source database first." });
      return;
    }

    setBusy("tables");
    setTableInfo(null);
    setOptions(DEFAULT_RESTORE_OPTIONS);
    setResult(null);

    await mutate("externalTables", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/external/tables`,
      payload,
      onSuccess: setTableInfo,
    });

    setBusy(null);
  };

  const tables = useMemo(
    () =>
      (tableInfo?.tables || []).map((t) => ({
        name: t.targetName || t.sourceName,
        rowCount: t.rowCount ?? null,
        supportsIdFilter: t.supportsIdFilter,
        disabled: !t.canImport,
        disabledReason: t.targetName
          ? "No column of this source table matches the local one."
          : "There is no table with this name in the live database.",
        extra: (
          <Space size={4}>
            <Tag color="default">from {t.sourceName}</Tag>
            {t.canImport && t.unmatchedColumns?.length > 0 && (
              <Tooltip title={`Ignored columns: ${t.unmatchedColumns.join(", ")}`}>
                <Tag color="warning">{t.unmatchedColumns.length} ignored</Tag>
              </Tooltip>
            )}
          </Space>
        ),
      })),
    [tableInfo]
  );

  const selectedNames = useMemo(
    () =>
      options.mode === RESTORE_MODE.FULL
        ? tables.filter((t) => !t.disabled).map((t) => t.name)
        : options.tables || [],
    [options.mode, options.tables, tables]
  );

  const runImport = async () => {
    const payload = connection();
    setBusy("import");
    setResult(null);

    await mutate("externalRestore", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/external/restore`,
      payload: {
        connection: payload,
        mode: options.mode,
        strategy: options.strategy,
        tables: options.mode === RESTORE_MODE.SELECTIVE ? options.tables : undefined,
        ids: buildIdsPayload(options, selectedNames),
        skipSafetyBackup: options.skipSafetyBackup,
        archiveSource,
      },
      onSuccess: (data) => {
        setResult(data);
        onImported?.();
      },
      onError: (body) => setResult(body?.data ?? null),
    });

    setBusy(null);
  };

  const confirmImport = () => {
    if (options.mode === RESTORE_MODE.SELECTIVE && selectedNames.length === 0) {
      modal.warning({ title: "Nothing selected", content: "Pick at least one table to import." });
      return;
    }

    modal.confirm({
      title: "Import from the external database?",
      centered: true,
      okText: "Import",
      okType: "danger",
      content: (
        <span>
          {selectedNames.length} table{selectedNames.length === 1 ? "" : "s"} will be written into
          the live database from <Text code>{database}</Text>.
        </span>
      ),
      onOk: runImport,
    });
  };

  const engineMeta = ENGINE_OPTIONS.find((e) => e.value === engine);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title={
          <Space size={8}>
            <ApiOutlined />
            Source server
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            engine: DB_ENGINE.SQL_SERVER,
            port: 1433,
            encrypt: true,
            trustServerCertificate: true,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="engine" label="Database type">
                <Select
                  options={ENGINE_OPTIONS.map(({ value, label }) => ({ value, label }))}
                  onChange={(value) => {
                    const meta = ENGINE_OPTIONS.find((e) => e.value === value);
                    form.setFieldsValue({ port: meta?.defaultPort, database: undefined });
                    setDatabases([]);
                    setTableInfo(null);
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={16} md={10}>
              <Form.Item
                name="host"
                label="Host"
                rules={[{ required: true, message: "The host is required." }]}
              >
                <Input placeholder="46.99.129.211" autoComplete="off" />
              </Form.Item>
            </Col>

            <Col xs={8} md={4}>
              <Form.Item name="port" label="Port">
                <InputNumber
                  min={1}
                  max={65535}
                  style={{ width: "100%" }}
                  placeholder={String(engineMeta?.defaultPort ?? "")}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={4}>
              <Form.Item
                label={
                  <Space size={6}>
                    TLS
                    <Tooltip title="Encrypt the connection; 'trust certificate' accepts a self-signed server certificate, which most internal servers use.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <Space>
                  <Form.Item name="encrypt" valuePropName="checked" noStyle>
                    <Switch size="small" checkedChildren="on" unCheckedChildren="off" />
                  </Form.Item>
                  <Form.Item name="trustServerCertificate" valuePropName="checked" noStyle>
                    <Switch size="small" checkedChildren="trust" unCheckedChildren="verify" />
                  </Form.Item>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: "The username is required." }]}
              >
                <Input autoComplete="off" />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: "The password is required." }]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item name="database" label="Database">
                <Select
                  showSearch
                  allowClear
                  placeholder={databases.length ? "Pick a database" : "Load the list first"}
                  options={databases.map((d) => ({ value: d, label: d }))}
                  onChange={() => setTableInfo(null)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={4}>
              <Form.Item label=" ">
                <Button
                  variant="secondary"
                  block
                  icon={<ReloadOutlined />}
                  loading={busy === "databases"}
                  onClick={loadDatabases}
                >
                  Load
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Space wrap>
            <Button
              variant="secondary"
              icon={<ApiOutlined />}
              loading={busy === "test"}
              onClick={testConnection}
            >
              Test connection
            </Button>

            <Button
              variant="primary"
              icon={<DatabaseOutlined />}
              loading={busy === "tables"}
              disabled={!database}
              onClick={loadTables}
            >
              Inspect tables
            </Button>
          </Space>

          {testResult && (
            <Alert
              style={{ marginTop: 16 }}
              type={testResult.connected ? "success" : "error"}
              showIcon
              message={
                testResult.connected
                  ? `Connected in ${testResult.elapsedMs} ms`
                  : "Could not connect"
              }
              description={testResult.connected ? testResult.serverVersion : testResult.error}
            />
          )}

          <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
            These credentials are used only for this request and are never stored.
          </Paragraph>
        </Form>
      </Card>

      {tableInfo && !result && (
        <Card
          title={
            <Space size={8}>
              <CloudDownloadOutlined />
              What to import from {tableInfo.database}
            </Space>
          }
        >
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {tableInfo.missingInSource?.length > 0 && (
              <Alert
                type="info"
                showIcon
                message={`${tableInfo.missingInSource.length} local tables have no counterpart on the source and will be left untouched.`}
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {tableInfo.missingInSource.join(", ")}
                  </Text>
                }
              />
            )}

            <RestoreOptions tables={tables} value={options} onChange={setOptions} />

            <Space size={10}>
              <Switch checked={archiveSource} onChange={setArchiveSource} />
              <Text>Save what is imported as a backup archive</Text>
              <Tooltip title="Writes the rows pulled from the source into a .kbak file first, so the import is auditable and can be replayed later.">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>

            <Button
              variant="danger"
              icon={<CloudDownloadOutlined />}
              loading={busy === "import"}
              onClick={confirmImport}
            >
              Import into the live database
            </Button>
          </Space>
        </Card>
      )}

      {result && (
        <Card
          title="Import result"
          extra={
            <Button variant="secondary" onClick={() => setResult(null)}>
              Back to selection
            </Button>
          }
        >
          <RestoreResult result={result} />
        </Card>
      )}
    </Space>
  );
};

export default ExternalRestorePanel;
